import { useState } from 'react';
import { Reference } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { mdiFileRemove, mdiFileRestore } from '@mdi/js';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';

import {
    ArchiveTrainingsDocument,
    ISortDirection,
    ITrainingManagementListItemFragment,
    ITrainingSortBy,
    ITrainingSortByInput,
} from 'graphql/types';
import {
    TableActionBar,
    TableCell,
    TableHead,
    TableSortButton,
    useSelection,
} from 'common/components/Table';
import { Checkbox } from 'common/components/Checkbox';
import { IconButton } from 'common/components/IconButton';
import { Tooltip } from 'common/components/Tooltip';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { useAppPermissions } from 'user/hooks/usePermissions';

import { TrainingsManagementTableRow } from './TrainingsManagementTableRow';

interface IProps {
    trainings: ITrainingManagementListItemFragment[];
    archived?: boolean;
    currentSort: ITrainingSortByInput;
    onSort: (
        sortField: ITrainingSortBy,
        initialDirection?: ISortDirection
    ) => void;
}

export const TrainingsManagementTable = ({
    trainings,
    archived = false,
    currentSort,
    onSort,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();

    const [showArchiveDialog, setShowArchiveDialog] = useState(false);

    const { canArchive = false } = useAppPermissions('training', 'management');

    const selectionProps = useSelection(trainings);
    const { isSelected, rows, selected, setSelected, onSelectAll } =
        selectionProps;

    const [archiveTrainings, { loading: loadingArchive }] = useMutation(
        ArchiveTrainingsDocument
    );

    if (!trainings.length) return null;

    // On the active tab we archive (move to trash); on the trash tab we restore.
    const isArchiveAction = !archived;

    const handleArchive = async () => {
        const selectedCount = selected.length;

        try {
            const response = await archiveTrainings({
                variables: { ids: selected, archive: isArchiveAction },
                // The backend re-indexes (OpenSearch) after archiving, so a plain
                // evict + refetch can briefly show the archived trainings again.
                // Instead optimistically remove them from the cached list so they
                // disappear immediately and stay gone.
                optimisticResponse: {
                    archiveTrainings: {
                        __typename: 'ArchiveTrainings',
                        ok: true,
                    },
                },
                update: (cache, { data }) => {
                    // The mutation only returns `ok`, so the archived ids are the
                    // ones we requested.
                    if (!data?.archiveTrainings?.ok) return;

                    const archivedIds = new Set(selected);

                    cache.modify({
                        fields: {
                            paginatedTrainings(existing, { readField }) {
                                if (!existing?.edges) return existing;

                                const edges = existing.edges.filter(
                                    (edge: { node: Reference }) =>
                                        !archivedIds.has(
                                            readField<string>(
                                                'id',
                                                edge.node
                                            ) ?? ''
                                        )
                                );

                                const removedCount =
                                    existing.edges.length - edges.length;

                                if (!removedCount) return existing;

                                return {
                                    ...existing,
                                    edges,
                                    count: Math.max(
                                        0,
                                        (existing.count ?? 0) - removedCount
                                    ),
                                };
                            },
                        },
                    });

                    cache.gc();
                },
            });

            if (!response.data?.archiveTrainings?.ok) {
                displaySnackbar(
                    translate(
                        `trainingActionError.${isArchiveAction ? 'archive' : 'unarchive'}`,
                        { count: selectedCount }
                    ),
                    { variant: 'error' }
                );

                return;
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowArchiveDialog(false);
        setSelected([]);

        displaySnackbar(
            translate(
                `trainingActionSuccess.${isArchiveAction ? 'archive' : 'unarchive'}`,
                { count: selectedCount }
            ),
            { variant: 'success' }
        );
    };

    const tableActions = (
        <Tooltip
            title={translate(isArchiveAction ? 'archiveItem' : 'unarchiveItem')}
        >
            <Box>
                <IconButton
                    aria-label={translate(
                        isArchiveAction ? 'archiveItem' : 'unarchiveItem'
                    )}
                    disabled={!isSelected}
                    iconPath={isArchiveAction ? mdiFileRemove : mdiFileRestore}
                    iconSize="2.4rem"
                    onClick={() => setShowArchiveDialog(true)}
                />
            </Box>
        </Tooltip>
    );

    return (
        <TableContainer>
            {canArchive && (
                <TableActionBar
                    actions={tableActions}
                    selectionProps={selectionProps}
                />
            )}

            <Table>
                <TableHead>
                    <TableRow>
                        {canArchive && (
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={false}
                                    disabled={isSelected}
                                    onChange={onSelectAll}
                                />
                            </TableCell>
                        )}
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {translate('title')}

                            <TableSortButton
                                currentSortDirection={currentSort.direction}
                                currentSortField={currentSort.field as string}
                                sortField={ITrainingSortBy.Title}
                                onClick={() => onSort(ITrainingSortBy.Title)}
                            />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {translate('trainingsManagementPage.groups')}

                            <TableSortButton
                                currentSortDirection={currentSort.direction}
                                currentSortField={currentSort.field as string}
                                sortField={ITrainingSortBy.GroupCount}
                                onClick={() =>
                                    onSort(ITrainingSortBy.GroupCount)
                                }
                            />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {translate('trainingsManagementPage.users')}

                            <TableSortButton
                                currentSortDirection={currentSort.direction}
                                currentSortField={currentSort.field as string}
                                sortField={ITrainingSortBy.UserCount}
                                onClick={() =>
                                    onSort(ITrainingSortBy.UserCount)
                                }
                            />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {translate('trainingsManagementPage.availability')}

                            <TableSortButton
                                currentSortDirection={currentSort.direction}
                                currentSortField={currentSort.field as string}
                                sortField={ITrainingSortBy.Availability}
                                onClick={() =>
                                    onSort(ITrainingSortBy.Availability)
                                }
                            />
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows?.map(({ index, isSelected, onSelect }) => {
                        const training = trainings[index];

                        if (!training) return null;

                        return (
                            <TrainingsManagementTableRow
                                isSelected={isSelected}
                                key={training.id}
                                selectable={canArchive}
                                training={training}
                                onSelect={onSelect}
                            />
                        );
                    })}
                </TableBody>
            </Table>

            <ConfirmDialog
                confirmText={translate(
                    isArchiveAction ? 'archiveItem' : 'unarchiveItem'
                )}
                loading={loadingArchive}
                open={showArchiveDialog}
                title={translate(
                    `trainingsManagementPage.archiveTrainingsDialog.${isArchiveAction ? 'titleArchive' : 'titleUnarchive'}`
                )}
                onCancel={() => setShowArchiveDialog(false)}
                onClose={() => setShowArchiveDialog(false)}
                onConfirm={handleArchive}
            >
                {translate(
                    `trainingsManagementPage.archiveTrainingsDialog.${isArchiveAction ? 'textArchive' : 'textUnarchive'}`
                )}
            </ConfirmDialog>
        </TableContainer>
    );
};
